import { useEffect, useState } from 'react';
import { useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { AI_COST_QUERY_OPTIONS } from '../utils/aiCostPresentation';

export function isAICostAccessError(error: unknown): boolean {
  const status = (error as { statusCode?: number } | null)?.statusCode;
  return status === 401 || status === 403;
}

/** Retain transient failures; revoke visible/cached privileged data on denied access. */
export function useAICostQuery<T>(queryKey: QueryKey, read: () => Promise<T>, enabled = true) {
  const client = useQueryClient();
  const [accessDenied, setAccessDenied] = useState(false);
  const keyHash = JSON.stringify(queryKey);
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      try {
        const result = await read();
        setAccessDenied(false);
        return result;
      } catch (error) {
        if (isAICostAccessError(error)) setAccessDenied(true);
        throw error;
      }
    },
    ...AI_COST_QUERY_OPTIONS,
    enabled: enabled && !accessDenied,
    gcTime: 0,
    retry: (failures, error) => !isAICostAccessError(error) && failures < 2,
  });
  useEffect(() => {
    if (accessDenied) client.removeQueries({ queryKey: JSON.parse(keyHash), exact: true });
  }, [accessDenied, client, keyHash]);
  return { ...query, data: accessDenied ? undefined : query.data, isError: accessDenied || query.isError, accessDenied };
}
